/*******************************************************
* FormForgotPassword
* 
* Multi-step form component for handling password reset workflow.
* Manages form state, validation, and API communication.
* 
* Key Features:
* - Three-step password reset process
* - Form validation and error handling
* - API integration for reset request, validation, and password change
********************************************************/
'use client';
//########## IMPORTS ##########
// #region IMPORTS
import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Alert,
    Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { FormTextField } from '@/src/components/common/FormTextField';
import useLogin from '@/src/app/(pages)/Login/hooks/UseLogin';
// #endregion


//########## TYPE DEFINITIONS ##########
//#region TYPE DEFINITIONS
interface ForgotPasswordFormProps {
    onSuccess: (message: string) => void;
    onCancel: () => void;
}

type FormState = 'REQUEST' | 'VALIDATE' | 'CHANGE';

interface FormData {
    username: string;
    pin: string;
    newPassword: string;
    confirmPassword: string;
    guid: string;
}

//#endregion


//########## CONSTANTS & Global Variables ##########
//#region CONSTANTS
const initialFormData: FormData = {
    username: '',
    pin: '',
    newPassword: '',
    confirmPassword: '',
    guid: '',
};

const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '4px',
    },
    '.MuiInputLabel-outlined:has(+ .MuiOutlinedInput-root > .MuiOutlinedInput-input:-webkit-autofill)': {
        transform: 'translate(14px, -9px) scale(0.75)',
    },
    '.MuiOutlinedInput-input:-webkit-autofill + .MuiOutlinedInput-notchedOutline > legend': {
        maxWidth: '100%',
    },
};
//#endregion


//########## FUNCTIONS ##########
// #region FUNCTIONS
const validatePin = (pin: string) => {
    return pin.length === 6 && /^\d+$/.test(pin);
};

const validatePassword = (password: string, confirmPassword: string) => {
    if (password !== confirmPassword) {
        return "Passwords don't match";
    }
    if (password.length < 8) {
        return "Password must be at least 8 characters";
    }
    return null;
};
//#endregion


export default function FormForgotPassword({ onSuccess, onCancel }: ForgotPasswordFormProps) {
    //########## MAIN LOGIC ##########
    // #region MAIN LOGIC
    const { handleSubmitPasswordReset: requestPasswordReset, validatePasswordResetPin, changePassword } = useLogin();
    const [formState, setFormState] = useState<FormState>('REQUEST');
    const [formData, setFormData] = useState<FormData>(initialFormData);
    const [error, setError] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [expirationDate, setExpirationDate] = useState<Date | null>(null);
    const [, setTick] = useState(0); // Force re-render every second

    // Calculate remaining time from expiration date
    const remainingTime = expirationDate
        ? Math.max(0, Math.floor((expirationDate.getTime() - Date.now()) / 1000))
        : 0;

    const handleSubmitRequest = async () => {
        if (!formData.username) {
            setFieldErrors({ username: 'Username or email is required' });
            return;
        }

        setIsSubmitting(true);
        setError(null);
        setFieldErrors({});

        try {
            const data = await requestPasswordReset({ username: formData.username });
            setFormData(prev => ({ ...prev, guid: data.GUID }));
            setExpirationDate(new Date(data.TimeExpires));
            setFormState('VALIDATE');
        } catch (error) {
            console.error('Error requesting password reset:', error);
            setError(error instanceof Error ? error.message : 'An error occurred. Please try again later.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmitValidate = async () => {
        if (!validatePin(formData.pin)) {
            setFieldErrors({ pin: 'Please enter a valid 6-digit PIN' });
            return;
        }

        setIsSubmitting(true);
        setError(null);
        setFieldErrors({});

        try {
            const data = await validatePasswordResetPin({
                GUID: formData.guid,
                PIN: parseInt(formData.pin)
            });

            if (data.ValidPIN) {
                setFormState('CHANGE');
            } else {
                setError('Invalid PIN. Please try again.');
            }
        } catch (error) {
            console.error('Error validating PIN:', error);
            setError(error instanceof Error ? error.message : 'An error occurred. Please try again later.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmitChange = async () => {
        const passwordError = validatePassword(formData.newPassword, formData.confirmPassword);
        if (passwordError) {
            setFieldErrors({ newPassword: passwordError });
            return;
        }

        setIsSubmitting(true);
        setError(null);
        setFieldErrors({});

        try {
            const result = await changePassword({
                GUID: formData.guid,
                PIN: parseInt(formData.pin),
                PASSWORD: formData.newPassword
            });

            if (result.success) {
                onSuccess(result.message || 'Your password has been successfully reset. You can now log in with your new password.');
            } else {
                setError(result.message || 'An error occurred while changing your password.');
            }
        } catch (error) {
            console.error('Error changing password:', error);
            setError('An error occurred. Please try again later.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
        if (event.key === 'Enter') {
            if (formState === 'REQUEST') {
                event.preventDefault();
                void handleSubmitRequest();
            } else if (formState === 'VALIDATE') {
                event.preventDefault();
                void handleSubmitValidate();
            }
        }
    }

    const handleChange = (field: keyof FormData, value: string) => {
        if (isSubmitting) return;
        setFormData(prev => ({ ...prev, [field]: value }));
        if (fieldErrors[field]) {
            setFieldErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
        setError(null);
    };

    // Timer management - trigger re-render every second to update the countdown display
    useEffect(() => {
        if (formState === 'VALIDATE') {
            const interval = setInterval(() => setTick(t => t + 1), 1000);
            return () => clearInterval(interval);
        }
    }, [formState]);

    useEffect(() => {
        if (formState === 'VALIDATE' && remainingTime === 0) {
            const timeout = setTimeout(() => {
                onCancel();
            }, 5000);
            return () => clearTimeout(timeout);
        }
    }, [remainingTime, formState, onCancel]);
    //#endregion

    //########## MARKDOWN ##########
    // #region MARKDOWN
    return (
        <Box
            component="form"
            noValidate
            onKeyDown={handleKeyDown}
            sx={{ mt: 1, width: '100%' }}
        >
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <FormTextField
                name="username"
                placeholder="Employee ID, email, or username"
                required
                sx={textFieldStyles}
                value={formData.username}
                onChange={(name, value) => handleChange(name as keyof FormData, value)}
                error={fieldErrors.username}
                disabled={formState !== 'REQUEST'}
                autoComplete="username"
            />
            {formState === 'REQUEST' && !fieldErrors.username && (
                <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 0.5 }}>
                    Enter one to receive a PIN to reset your password.
                </Typography>
            )}

            {formState === 'VALIDATE' && (
                <FormTextField

                    name="pin"
                    label="Enter PIN"
                    required
                    sx={textFieldStyles}
                    value={formData.pin}
                    onChange={(name, value) => handleChange(name as keyof FormData, value)}
                    error={fieldErrors.pin}
                    autoComplete="one-time-code"
                />
            )}

            {formState === 'CHANGE' && (
                <>
                    <FormTextField
                        name="newPassword"
                        label="New Password"
                        type="password"
                        required
                        sx={textFieldStyles}
                        value={formData.newPassword}
                        onChange={(name, value) => handleChange(name as keyof FormData, value)}
                        error={fieldErrors.newPassword}
                        autoComplete="new-password"
                    />
                    <FormTextField
                        name="confirmPassword"
                        label="Confirm New Password"
                        type="password"
                        required
                        sx={textFieldStyles}
                        value={formData.confirmPassword}
                        onChange={(name, value) => handleChange(name as keyof FormData, value)}
                        error={fieldErrors.confirmPassword}
                        autoComplete="new-password"
                    />
                </>
            )}

            {formState === 'VALIDATE' && (
                <Box mb={2}>
                    {remainingTime > 0 ? (
                        <div>Password reset PIN sent to email on file set to expire in: {String(Math.floor(remainingTime / 60)).padStart(2, '0')}:{String(remainingTime % 60).padStart(2, '0')}</div>
                    ) : (
                        <Typography color="error">Password reset PIN expired</Typography>
                    )}
                </Box>
            )}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                    onClick={onCancel}
                    variant="outlined"
                    sx={{ width: '45%' }}
                    disabled={isSubmitting}
                >
                    Cancel
                </Button>
                <LoadingButton
                    onClick={() => {
                        void (async () => {
                            switch (formState) {
                                case 'REQUEST':
                                    await handleSubmitRequest();
                                    break;
                                case 'VALIDATE':
                                    await handleSubmitValidate();
                                    break;
                                case 'CHANGE':
                                    await handleSubmitChange();
                                    break;
                            }
                        })();
                    }}
                    loading={isSubmitting}
                    loadingPosition="end"
                    variant="contained"
                    sx={{ width: '45%' }}
                    disabled={isSubmitting}
                >
                    {formState === 'REQUEST' && 'Submit'}
                    {formState === 'VALIDATE' && 'Verify'}
                    {formState === 'CHANGE' && 'Change Password'}
                </LoadingButton>
            </Box>
        </Box>
    );
    //#endregion
}
